import React, { useState, useEffect } from "react";
import axios from "axios";
import proxy from "../proxy";
import {
  Box,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  HStack,
  Skeleton,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";

const DividendKPI = ({ onCardClick }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [kpi, setKpi] = useState(null);

  useEffect(() => {
    axios
      .get(`${proxy}server_mts/GetDividendKPI.php`)
      .then((res) => setKpi(res.data))
      .catch(() => {});
  }, []);

  const monthlyDividends  = kpi?.monthlyDividends   ?? 0;
  const stockDividends    = kpi?.stockDividends      ?? [];
  const milestonesAchieved = kpi?.milestonesAchieved ?? 0;
  const totalMilestones   = kpi?.totalMilestones     ?? 72;
  const totalPaidDividends = kpi?.totalPaidDividends ?? 0;

  const totalShares = kpi?.totalShares ?? 0;
  const avgDivPerStock = totalShares > 0
    ? (monthlyDividends / totalShares).toFixed(2)
    : "0.00";

  const formatNumber = (v) =>
    Number(v).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  if (!kpi) {
    return (
      <Box maxW="900px" mx="auto" mt={{ base: 3, md: 6 }} px={{ base: 2, md: 3 }}>
        <Grid
          templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }}
          gap={{ base: 2, sm: 3, md: 4 }}
        >
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} height="100px" borderRadius="xl" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
          ))}
        </Grid>
      </Box>
    );
  }

  const cards = [
    {
      id: "monthly",
      label: "Monthly Dividends",
      shortLabel: "Monthly Div",
      emoji: "💰",
      value: `$${formatNumber(monthlyDividends)}`,
      valueSuffix: "/ month",
      color: "#48BB78",
      gradient: "linear-gradient(135deg,#48BB78,#68D391)",
      subtitle: "Click to view details",
      route: "/stocks",
    },
    {
      id: "avg",
      label: "Avg Div/Stock",
      shortLabel: "Avg/Stock",
      emoji: "💵",
      value: `$${avgDivPerStock}`,
      valueSuffix: "",
      color: "#4299E1",
      gradient: "linear-gradient(135deg,#4299E1,#63B3ED)",
      subtitle: "Based on all stocks",
      route: "/stocks",
    },
    {
      id: "milestones",
      label: "Milestones",
      shortLabel: "Milestones",
      emoji: "🎯",
      value: `${milestonesAchieved} / ${totalMilestones}`,
      valueSuffix: "",
      color: "#38B2AC",
      gradient: "linear-gradient(135deg,#38B2AC,#4FD1C5)",
      subtitle: "Progress overview",
      route: "/milestones",
      progress: (milestonesAchieved / totalMilestones) * 100,
    },
    {
      id: "total",
      label: "Total Dividends Paid",
      shortLabel: "Total Paid",
      emoji: "📦",
      value: `$${formatNumber(totalPaidDividends)}`,
      valueSuffix: "",
      color: "#ECC94B",
      gradient: "linear-gradient(135deg,#ECC94B,#F6E05E)",
      subtitle: "All time earnings",
      route: "/stocks",
    },
  ];

  const renderCard = (card) => {
    const isHovered = hoveredCard === card.id;

    return (
      <Box
        key={card.id}
        position="relative"
        onMouseEnter={() => setHoveredCard(card.id)}
        onMouseLeave={() => setHoveredCard(null)}
        onClick={() => onCardClick?.(card.route)}
        cursor="pointer"
      >
        {/* Glow */}
        <Box
          position="absolute"
          inset="-3px"
          borderRadius="xl"
          bg={card.gradient}
          opacity={isHovered ? 0.3 : 0}
          filter="blur(18px)"
          transition="0.35s"
        />

        {/* Card */}
        <Box
          bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
          p={{ base: 3, sm: 4, md: 5 }}
          borderRadius="xl"
          border="2px solid"
          borderColor={isHovered ? card.color : "rgba(255,221,64,0.25)"}
          transition="0.35s"
          transform={isHovered ? "translateY(-6px) scale(1.02)" : "scale(1)"}
          boxShadow={
            isHovered
              ? `0 20px 40px ${card.color}44`
              : "0 10px 25px rgba(0,0,0,0.35)"
          }
          position="relative"
          height="100%"
          display="flex"
          flexDirection="column"
          minH={{ base: "auto", md: "unset" }}
        >
          {/* Corner decoration */}
          <Box
            position="absolute"
            top={2}
            right={2}
            w={{ base: "16px", md: "22px" }}
            h={{ base: "16px", md: "22px" }}
            borderTop="2px solid"
            borderRight="2px solid"
            borderColor={card.color}
            borderRadius="0 8px 0 0"
            opacity={isHovered ? 0.6 : 0.3}
            transition="0.35s"
          />

          <Stat>
            {/* Label — short version on mobile, full on desktop */}
            <HStack justify="space-between">
              <StatLabel
                color="gray.400"
                fontSize={{ base: "xs", md: "sm" }}
                fontWeight="bold"
                letterSpacing="wider"
                noOfLines={1}
              >
                {card.emoji}{" "}
                <Text
                  as="span"
                  display={{ base: "none", md: "inline" }}
                >
                  {card.label}
                </Text>
                <Text
                  as="span"
                  display={{ base: "inline", md: "none" }}
                >
                  {card.shortLabel}
                </Text>
              </StatLabel>

              <ArrowForwardIcon
                color={card.color}
                opacity={isHovered ? 1 : 0}
                transform={isHovered ? "translateX(0)" : "translateX(-5px)"}
                transition="0.25s"
                boxSize={{ base: 3, md: 4 }}
              />
            </HStack>

            {/* Value */}
            <StatNumber
              fontSize={{ base: "lg", sm: "xl", md: "2xl" }}
              fontWeight="black"
              background={card.gradient}
              backgroundClip="text"
              sx={{ WebkitTextFillColor: "transparent" }}
              textShadow={`0 0 20px ${card.color}33`}
              mt={1}
              lineHeight="1.2"
            >
              {card.value}
              {card.valueSuffix && (
                <Text
                  as="span"
                  fontSize={{ base: "xs", md: "sm" }}
                  fontWeight="medium"
                  display={{ base: "none", sm: "inline" }}
                >
                  {" "}{card.valueSuffix}
                </Text>
              )}
            </StatNumber>

            {/* Progress bar */}
            {card.progress !== undefined && (
              <Box
                w="full"
                h={{ base: "4px", md: "5px" }}
                bg="rgba(255,255,255,0.1)"
                borderRadius="full"
                mt={2}
                overflow="hidden"
              >
                <Box
                  h="full"
                  bg={card.gradient}
                  borderRadius="full"
                  width={`${card.progress}%`}
                  transition="width 0.8s ease"
                  boxShadow={`0 0 12px ${card.color}55`}
                />
              </Box>
            )}

            {/* Subtitle — hidden on very small screens */}
            <HStack
              fontSize="xs"
              color="gray.500"
              mt={{ base: 1, md: 2 }}
              display={{ base: "none", sm: "flex" }}
            >
              <Box
                boxSize="4px"
                bg={card.color}
                borderRadius="full"
                animation="pulse 2s ease-in-out infinite"
                flexShrink={0}
              />
              <Text noOfLines={1}>{card.subtitle}</Text>
            </HStack>
          </Stat>
        </Box>
      </Box>
    );
  };

  return (
    <Box maxW="900px" mx="auto" mt={{ base: 3, md: 6 }} px={{ base: 2, md: 3 }}>
      <Grid
        templateColumns={{
          base: "repeat(2, 1fr)",  // mobile: 2x2 grid
          md: "repeat(4, 1fr)",    // desktop: 4 coloane
        }}
        gap={{ base: 2, sm: 3, md: 4 }}
        alignItems="stretch"
      >
        {cards.map(renderCard)}
      </Grid>

      {/* Info Bar */}
      <Box
        mt={{ base: 3, md: 4 }}
        p={{ base: 2, md: 3 }}
        bg="rgba(26,32,44,0.6)"
        borderRadius="lg"
        border="1px solid rgba(255,221,64,0.2)"
      >
        <HStack
          justify="space-around"
          fontSize={{ base: "2xs", sm: "xs" }}
          color="gray.400"
          flexWrap="wrap"
          spacing={{ base: 1, md: 3 }}
        >
          <Text>Dividend Tracking</Text>
          <Text display={{ base: "none", sm: "block" }}>•</Text>
          <Text>Monthly: ${formatNumber(monthlyDividends)}</Text>
          <Text display={{ base: "none", sm: "block" }}>•</Text>
          <Text>Annual: ${formatNumber(monthlyDividends * 12)}</Text>
        </HStack>
      </Box>
    </Box>
  );
};

export default DividendKPI;